import { AlertTriangle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import type { Commit } from '../../types/api';

interface DeleteCommitDialogProps {
  commit: Commit | null;
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  loading?: boolean;
}

export default function DeleteCommitDialog({
  commit,
  open,
  onClose,
  onConfirm,
  loading = false,
}: DeleteCommitDialogProps) {
  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader className="pb-4 border-b border-[hsl(var(--border))]">
          <DialogTitle className="font-mono flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-[hsl(var(--destructive))]" />
            Delete Commit
          </DialogTitle>
        </DialogHeader>

        <div className="py-4">
          <DialogDescription className="text-[hsl(var(--foreground))]">
            Are you sure you want to delete this commit?
          </DialogDescription>

          {commit && (
            <div className="mt-4 p-3 bg-[hsl(var(--muted))] rounded-md font-mono text-sm">
              <span className="text-[hsl(var(--muted-foreground))]">{commit.type}:</span>{' '}
              {commit.message}
            </div>
          )}

          <p className="mt-4 text-sm text-[hsl(var(--muted-foreground))]">
            This action cannot be undone. All reactions, comments, and associated data will be permanently deleted.
          </p>
        </div>

        <DialogFooter className="pt-4 border-t border-[hsl(var(--border))]">
          <Button variant="outline" onClick={onClose} disabled={loading} className="w-full sm:w-auto">
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            loading={loading}
            className="w-full sm:w-auto"
          >
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
